import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import type { IsoWeekday } from "../client-messages";

/**
 * When the daily RoadSense run happens: 06:00 Sydney wall-clock, every day.
 * Mirrors `ROADSENSE_SCHEDULE` in `plugins/roadsense/window.ts` (a test keeps
 * them in step); not imported, so the app bundle doesn't pull in the plugin.
 */
const RUN_TIME_ZONE = "Australia/Sydney";
const RUN_TIME = "06:00:00";

const DAY_MS = 24 * 60 * 60 * 1000;

/** What the viewer sees run times in; `now` picks the coming run, so the zone's daylight saving is right. */
export interface ReportUpdateTimeContext {
  readonly now: Date;
  /** The viewer's IANA timezone. One the runtime doesn't know falls back to UTC. */
  readonly viewerTimeZone: string;
}

export interface ReportUpdateTimeInput extends ReportUpdateTimeContext {
  /** A weekly delivery's Sydney send day; the label then names the viewer's day when it differs. */
  readonly weekday?: IsoWeekday | undefined;
}

/**
 * The time the report updates, in the viewer's timezone with its abbreviation:
 * "6:00 am AEDT" in Sydney, "3:00 am AWST" in Perth. For a weekly delivery whose
 * Sydney send day falls on another day for the viewer, the day is named too:
 * "7:00 pm GMT on Sunday" in London for a Monday delivery.
 */
export function reportUpdateTimeLabel({ now, viewerTimeZone, weekday }: ReportUpdateTimeInput): string {
  const timeZone = isKnownTimeZone(viewerTimeZone) ? viewerTimeZone : "UTC";
  const runDay = nextRunDay(now, weekday);
  const run = fromZonedTime(`${runDay}T${RUN_TIME}`, RUN_TIME_ZONE);

  const time = formatInTimeZone(run, timeZone, "h:mm a").toLowerCase();
  const label = `${time} ${timeZoneAbbreviation(run, timeZone)}`;
  if (weekday === undefined || formatInTimeZone(run, timeZone, "yyyy-MM-dd") === runDay) return label;
  return `${label} on ${formatInTimeZone(run, timeZone, "EEEE")}`;
}

/** The Sydney date (`YYYY-MM-DD`) of the next run at or after `now`, on `weekday` when given. */
function nextRunDay(now: Date, weekday: IsoWeekday | undefined): string {
  let day = formatInTimeZone(now, RUN_TIME_ZONE, "yyyy-MM-dd");
  if (fromZonedTime(`${day}T${RUN_TIME}`, RUN_TIME_ZONE) < now) day = addDays(day, 1);
  while (weekday !== undefined && isoWeekdayOf(day) !== weekday) day = addDays(day, 1);
  return day;
}

function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

function isoWeekdayOf(day: string): number {
  return new Date(`${day}T00:00:00Z`).getUTCDay() || 7;
}

function isKnownTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** en-AU names Australian zones (AEDT, AWST, ACST); others may read like "GMT+1". */
function timeZoneAbbreviation(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-AU", { timeZone, timeZoneName: "short" }).formatToParts(at);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? timeZone;
}
